import 'react-advanced-cropper/dist/style.css'
import 'react-advanced-cropper/dist/themes/compact.css'

import { observer } from 'mobx-react-lite'
import { RefObject, useCallback, useMemo } from 'react'
import {
    CircleStencil,
    Cropper,
    CropperRef,
    CropperState,
    ImageRestriction,
    RectangleStencil,
    Size,
} from 'react-advanced-cropper'

import { EditContext } from './types'
import { EditableImageBackground } from './EditableImageBackground'

export type ImageEditorProps = {
    cropperRef: RefObject<CropperRef>
    src?: string
    context: EditContext
    onReady?: () => void
}

//
// This will return a Blog of the modified image or undefined for any cancelation or no changes
//

export const ImageCropper = observer(({ cropperRef, src, context, onReady }: ImageEditorProps) => {
    const cropperEnabled = context.config.crop && context.mode === 'crop'

    const getDefaultSize = useCallback(
        () =>
            ({ imageSize, visibleArea }: CropperState) => {
                return {
                    width: (visibleArea || imageSize).width,
                    height: (visibleArea || imageSize).height,
                } satisfies Size
            },
        []
    )

    const stencilProps = useMemo(() => {
        if (!cropperEnabled) {
            return {
                movable: false,
                resizable: false,
                lines: false,
                handlers: false,
                overlayClassName: 'text-black/9',
            }
        }

        const aspectRatioValue = context.config.crop?.aspectRatio
        let aspectRatio: number | undefined

        if (aspectRatioValue) {
            const aspectRatios = aspectRatioValue.split('/').map((s) => Number.parseInt(s))
            aspectRatio = aspectRatios.length === 1 ? aspectRatios[0] : aspectRatios[0] / aspectRatios[1]
        }

        return {
            movable: true,
            resizable: true,
            ...(aspectRatio ? { aspectRatio } : {}),
            lines: true,
            handlers: true,
            overlayClassName: '',
        }
    }, [context.config.crop, cropperEnabled])

    const backgroundWrapperProps = useMemo(
        () => ({
            scaleImage: cropperEnabled,
            moveImage: cropperEnabled,
        }),
        [cropperEnabled]
    )

    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <Cropper
                ref={cropperRef}
                src={src}
                // @ts-expect-error todo fix thids
                defaultSize={getDefaultSize}
                imageRestriction={ImageRestriction.fitArea}
                stencilComponent={context.config.image.shape === 'circle' ? CircleStencil : RectangleStencil}
                stencilProps={stencilProps}
                backgroundWrapperProps={backgroundWrapperProps}
                backgroundComponent={EditableImageBackground}
                backgroundProps={{ context }}
                onReady={onReady}
                className="min-h-0 flex-1"
            />
        </div>
    )
})
